import { computed, inject, Injectable, signal } from '@angular/core';
import {
    DocumentBatchEntity,
    DocumentBatchItemStatusEnum,
    DocumentBatchStatusEnum,
    batchHasZip,
    batchIsRunning,
} from '@domain';
import { TranslateService } from '@ngx-translate/core';
import { RaToastService } from '@shared/components';
import { finalize, Observable, Subscription } from 'rxjs';
import { DocumentBatchController } from 'src/app/domain/controllers/document/document-batch.controller';
import { downloadFileFromUrl, openFileInNewTab } from '../utils/file-download.util';
import { PusherService } from '../utils/pusher/pusher.service';

/**
 * Orquestra o pacote de PDFs em lote: pede, acompanha e entrega o ZIP.
 *
 * Mesmo desenho do `DocumentPdfGenerationService` e pelo mesmo motivo: o serviço é dono
 * do ciclo inteiro e assina o Pusher por conta própria, então a geração **sobrevive à
 * navegação**. Minimizar o modal não pausa nada — só troca a janela pela ilha do rodapé.
 *
 * Um pacote por vez (singleton em root): o limite é da API (um por serviço) e aqui vale
 * para a aba inteira, porque o selo minimizado é um só.
 */
@Injectable({ providedIn: 'root' })
export class DocumentBatchService {
    private _controller = inject(DocumentBatchController);
    private _pusherService = inject(PusherService);
    private _toast = inject(RaToastService);
    private _translate = inject(TranslateService);

    /** Pacote em foco: o que está rodando ou o último pronto do serviço aberto. */
    public readonly batch = signal<DocumentBatchEntity | null>(null);
    /** `true` enquanto o modal está fechado mas o pacote continua andando. */
    public readonly minimized = signal(false);
    /** Trava o botão "Baixar em lote" enquanto o pedido está sendo criado. */
    public readonly starting = signal(false);
    /**
     * Baixando o ZIP.
     *
     * O arquivo é buscado inteiro como blob antes de ir para o disco (é o que dá nome ao
     * arquivo e escapa do bloqueador de pop-up), e isso leva segundos num pacote grande.
     * Sem este estado, o clique em "Baixar ZIP" não mostrava nada acontecendo.
     */
    public readonly downloading = signal(false);

    public readonly running = computed(() => batchIsRunning(this.batch()));
    public readonly ready = computed(() => batchHasZip(this.batch()));

    public readonly total = computed(() => this.batch()?.items?.length ?? 0);
    public readonly done = computed(
        () =>
            this.batch()?.items?.filter(
                item => item.status !== DocumentBatchItemStatusEnum.PENDING
            ).length ?? 0
    );
    public readonly succeeded = computed(
        () =>
            this.batch()?.items?.filter(
                item => item.status === DocumentBatchItemStatusEnum.COMPLETED
            ).length ?? 0
    );
    public readonly failed = computed(() => this.total() - this.succeeded());

    /**
     * Fase de montagem do pacote: os PDFs acabaram e o que roda agora é o ZIP.
     *
     * Vale para o modal, a ilha do rodapé e o card do serviço — os três mostravam "100%"
     * bem no meio da etapa mais demorada, dando a entender que tinha acabado.
     *
     * Não espera o status `ZIPPING` do servidor: entre o último documento ficar pronto e
     * esse evento chegar existe uma janela em que nada se mexia na tela.
     */
    public readonly zipping = computed(() => {
        if (!this.running()) return false;
        if (this.batch()?.status === DocumentBatchStatusEnum.ZIPPING) return true;

        const total = this.total();

        return total > 0 && this.done() === total;
    });
    /**
     * Quanto do ZIP já foi costurado, por BYTES quando o servidor conseguiu medir.
     *
     * Contar arquivos engana num pacote onde um relatório tem 22 MB e o seguinte, 400 KB:
     * a barra salta e depois trava. Sem os tamanhos (o Spaces não respondeu), cai para a
     * contagem de arquivos, que é melhor do que nada.
     */
    public readonly zipPercent = computed(() => {
        const totalBytes = this.zipTotalBytes();
        if (totalBytes > 0) {
            return Math.min(100, Math.round((this.zipBytes() / totalBytes) * 100));
        }

        const total = this.zipTotal();

        return total ? Math.round((this.zipDone() / total) * 100) : 0;
    });

    public readonly percent = computed(() => {
        const total = this.total();
        return total ? Math.round((this.done() / total) * 100) : 0;
    });

    /**
     * Quanto a ilha de "pacote pronto" fica na tela.
     *
     * Ela é um aviso, não o lugar do download: quem perder os 5 segundos tem a faixa verde
     * na tela do serviço e a notificação no sino, que não somem.
     */
    private static readonly READY_PILL_MS = 5_000;

    /**
     * Quantos documentos da frente da fila são acompanhados de perto.
     *
     * O servidor mantém cinco em render ao mesmo tempo; dez cobre a frente da fila com
     * folga sem encher a conexão do Pusher de canais num pacote de 40 documentos.
     */
    private static readonly MAX_DOCUMENT_CHANNELS = 10;

    /**
     * Pacotes que o usuário já dispensou no "X".
     *
     * Dispensar é definitivo: aquele aviso não volta nem depois de um F5, nem ao entrar no
     * serviço de novo. Só um pacote NOVO (outro `_id`) aparece. Guardamos os últimos ids
     * porque o aviso é por pacote, não por serviço.
     */
    private static readonly DISMISSED_STORAGE_KEY = 'rapidocs:document-batch-dismissed';
    private static readonly DISMISSED_KEEP = 30;

    /**
     * Progresso do documento que o engine está desenhando agora, por id.
     *
     * Vem do canal do DOCUMENTO (`DOCUMENT-<id>`, evento `doc-pdf-progress`) — o mesmo que
     * o PDF avulso usa. O canal do pacote só sabe dizer pronto/na fila: sem isto, um
     * relatório de 20 páginas fica "na fila" por minutos sem nada se mexer.
     */
    private readonly _renderProgress = signal<Record<string, number>>({});
    /** Documentos que o worker reportou em render neste ciclo. */
    private readonly _rendering = signal<Set<string>>(new Set());

    /**
     * Arquivos já costurados no ZIP, durante a montagem.
     *
     * Um pacote de meio giga leva minutos nessa etapa. Sem este contador a tela dizia só
     * "montando o pacote ZIP", e demora e travamento ficavam iguais.
     */
    public readonly zipDone = signal(0);
    public readonly zipTotal = signal(0);
    /** Bytes já escritos no ZIP e o total medido antes de começar. */
    public readonly zipBytes = signal(0);
    public readonly zipTotalBytes = signal(0);

    /**
     * Documentos com um "pular" ou "tentar de novo" em voo.
     *
     * Trava o par de botões daquela linha: o item só muda de estado quando a API
     * responde, e sem isto dois cliques seguidos mandam duas vezes a mesma ação.
     */
    private readonly _busyItems = signal<Set<string>>(new Set());

    public isItemBusy(documentId: string): boolean {
        return this._busyItems().has(documentId);
    }

    /** `0` quando o engine ainda não reportou nada para aquele documento. */
    public itemPercent(documentId: string): number {
        return this._renderProgress()[documentId] ?? 0;
    }

    public isRendering(documentId: string): boolean {
        return this._rendering().has(documentId);
    }

    private _channelSub: Subscription | null = null;
    /** Uma assinatura por documento em render, desfeita assim que ele sai da fila. */
    private _renderSubs = new Map<string, Subscription>();
    private _pillId: string | null = null;
    private _readyPillTimer: ReturnType<typeof setTimeout> | null = null;

    // * ── Ciclo ────────────────────────────────────────────────────────────────

    /** Passo 2 começa aqui: cria o pacote e passa a ouvir o canal dele. */
    public start(orderId: string, documentIds: string[]): void {
        if (this.running() || this.starting()) return;

        this.starting.set(true);
        this._controller.create(orderId, documentIds).subscribe({
            next: batch => {
                this.starting.set(false);
                this.adopt(batch);
            },
            error: () => this.starting.set(false),
        });
    }

    /**
     * Assume um pacote que já existe: o que a tela do serviço encontra ao abrir (F5 no
     * meio da geração, ou o pacote pronto de minutos atrás).
     */
    public adopt(batch: DocumentBatchEntity | null): void {
        if (!batch) return;
        // Pacote pronto que a pessoa já mandou embora não volta à tela. Um que ainda está
        // gerando continua aparecendo: ali o aviso é o andamento, não o resultado.
        if (batchHasZip(batch) && DocumentBatchService._wasDismissed(batch._id)) return;
        if (this.batch()?._id === batch._id && this._channelSub) {
            this.batch.set(batch);
            // A lista pode ter mudado de forma (um item voltou para a fila num
            // "tentar de novo"): a frente da fila é outra, os canais também.
            this._syncDocumentChannels();
            return;
        }

        this._unlisten();
        this.batch.set(batch);

        if (!batchIsRunning(batch)) return;

        this._listen(batch._id);
        this._syncDocumentChannels();
    }

    /** Fecha o modal e deixa a ilha do rodapé cuidando do acompanhamento. */
    public minimize(): void {
        if (!this.running()) return;

        this.minimized.set(true);
        this._showPill();
    }

    /** Reabre o modal (clique na ilha). */
    public restore(): void {
        this.minimized.set(false);
        this._dismissPill();
    }

    public cancel(): void {
        const batch = this.batch();
        if (!batch) return;

        this._finishLocally(DocumentBatchStatusEnum.CANCELLED);
        this._controller.cancel(batch._id).subscribe();
    }

    /**
     * Tira este documento do pacote.
     *
     * É a saída do "41 de 42": o documento travado vira falha na hora e, se era o último
     * pendente, o ZIP começa a ser montado com o que ficou pronto. O PDF dele não é
     * cobrado e continua podendo ser gerado sozinho, pela tela do documento.
     */
    public skipItem(documentId: string): void {
        this._runItemAction(documentId, id => this._controller.skipItem(id, documentId));
    }

    /**
     * Devolve à fila um documento que falhou.
     *
     * Num pacote já fechado isto o reabre e o ZIP é montado de novo no fim — por isso o
     * botão de baixar some enquanto a nova rodada acontece: o arquivo anterior saiu sem
     * este documento.
     */
    public retryItem(documentId: string): void {
        this._runItemAction(documentId, id => this._controller.retryItem(id, documentId));
    }

    private _runItemAction(
        documentId: string,
        action: (batchId: string) => Observable<DocumentBatchEntity>
    ): void {
        const batch = this.batch();
        if (!batch || this.isItemBusy(documentId)) return;

        this._setItemBusy(documentId, true);

        action(batch._id)
            // `finalize` e não os callbacks: o controller trata o erro com toast e devolve
            // um observable que COMPLETA sem emitir, então soltar a linha só no `next` ou
            // no `error` deixava o botão desabilitado para sempre.
            .pipe(finalize(() => this._setItemBusy(documentId, false)))
            .subscribe({
                next: fresh => {
                    // A resposta já traz o pacote inteiro atualizado: adotar é o que religa
                    // os canais quando o item volta para a fila.
                    if (fresh) this.adopt(fresh);
                },
                error: () => undefined,
            });
    }

    private _setItemBusy(documentId: string, busy: boolean): void {
        this._busyItems.update(current => {
            const next = new Set(current);
            busy ? next.add(documentId) : next.delete(documentId);

            return next;
        });
    }

    /**
     * Baixa o ZIP: pede a URL assinada e busca o arquivo como blob.
     *
     * O mesmo motivo do PDF avulso (ver `file-download.util.ts`): o arquivo está em
     * outra origem, então `download` num link é ignorado, e fora de um gesto do usuário
     * a aba nova morre no bloqueador de pop-up.
     */
    /**
     * @param batch Pacote a baixar. Sem ele, o que está em foco. A tela do serviço passa
     * o dela quando a pílula já foi dispensada: o ZIP continua no servidor.
     */
    public downloadZip(batch: DocumentBatchEntity | null = this.batch()): void {
        if (!batch || !batchHasZip(batch) || this.downloading()) return;

        this.downloading.set(true);
        this._pillDownloading();

        this._controller.getDownloadUrl(batch._id).subscribe({
            next: async resp => {
                try {
                    if (!resp?.url) return;

                    const downloaded = await downloadFileFromUrl(resp.url);
                    if (!downloaded) openFileInNewTab(resp.url);
                } finally {
                    this._endDownload();
                }
            },
            error: () => {
                this._endDownload();
                // Pode ser rede, pode ser o pacote ter saído de cena porque alguém
                // editou um documento do serviço. Quem sabe qual é o caso é o servidor:
                // se ele não tem mais pacote para este serviço, a tela larga o dela.
                this._recheckOrderPackage();
            },
        });
    }

    /** Rebusca o pacote do serviço e solta o local quando o servidor não tem mais nenhum. */
    public recheckOrderPackage(): void {
        this._recheckOrderPackage();
    }

    private _recheckOrderPackage(): void {
        const orderId = this.batch()?.orderId;
        if (!orderId) return;

        this._controller.getCurrentByOrder(orderId).subscribe({
            next: fresh => {
                if (fresh) {
                    this.adopt(fresh);
                    return;
                }

                this._unlisten();
                this._dismissPill();
                this.minimized.set(false);
                this.batch.set(null);
            },
        });
    }

    private _endDownload(): void {
        this.downloading.set(false);
        if (this._pillId) this._showReadyPill();
    }

    /** Some com o pacote pronto da tela (o "X" da pílula). Não apaga o ZIP no servidor. */
    public dismiss(): void {
        const batchId = this.batch()?._id;

        this._unlisten();
        this._dismissPill();
        this.minimized.set(false);
        this.batch.set(null);

        if (batchId) DocumentBatchService._rememberDismissed(batchId);
    }

    private static _dismissedIds(): string[] {
        try {
            const raw = localStorage.getItem(DocumentBatchService.DISMISSED_STORAGE_KEY);
            const parsed = raw ? JSON.parse(raw) : [];

            return Array.isArray(parsed) ? parsed.filter(id => typeof id === 'string') : [];
        } catch {
            // Storage bloqueado ou conteúdo estragado: o pior caso é o aviso reaparecer.
            return [];
        }
    }

    private static _wasDismissed(batchId: string): boolean {
        return DocumentBatchService._dismissedIds().includes(batchId);
    }

    private static _rememberDismissed(batchId: string): void {
        try {
            const ids = DocumentBatchService._dismissedIds().filter(id => id !== batchId);
            ids.push(batchId);

            localStorage.setItem(
                DocumentBatchService.DISMISSED_STORAGE_KEY,
                // Só os últimos: a lista não pode crescer para sempre no storage.
                JSON.stringify(ids.slice(-DocumentBatchService.DISMISSED_KEEP))
            );
        } catch {
            // Sem storage o "X" vale só para esta sessão.
        }
    }

    // * ── Canal ────────────────────────────────────────────────────────────────

    private _listen(batchId: string): void {
        const channelName = `BATCH-${batchId}`;
        const sub = new Subscription();

        sub.add(
            this._pusherService
                .subscribe(channelName, 'doc-batch-progress')
                .subscribe(resp => this._onProgress(resp))
        );

        sub.add(
            this._pusherService
                .subscribe(channelName, 'doc-batch-completed')
                .subscribe(resp => this._onCompleted(resp?.status))
        );

        sub.add(
            this._pusherService
                .subscribe(channelName, 'doc-batch-error')
                .subscribe(() => this._onError())
        );

        // Eventos publicados durante uma queda de conexão se perdem — inclusive o
        // "terminou". Rebusca o pacote no servidor ao reconectar.
        sub.add(this._pusherService.onReconnected().subscribe(() => this._resync()));

        this._channelSub = sub;
    }

    /**
     * Assina o canal dos PRÓXIMOS documentos da fila e larga o de quem já saiu dela.
     *
     * O gatilho não é o servidor dizer "este está em render": entre um ciclo do worker e
     * outro vão 15 segundos, e o engine pode nem passar por `CREATING` no caminho. Quem
     * avisa que a geração começou é o próprio engine, pelo canal do DOCUMENTO — o mesmo
     * `doc-pdf-progress` que o PDF avulso escuta. Por isso assinamos a frente da fila e
     * deixamos o primeiro evento dizer quem começou.
     */
    private _syncDocumentChannels(): void {
        const batch = this.batch();

        const wanted = batchIsRunning(batch)
            ? new Set(
                  (batch?.items ?? [])
                      .filter(item => item.status === DocumentBatchItemStatusEnum.PENDING)
                      .slice(0, DocumentBatchService.MAX_DOCUMENT_CHANNELS)
                      .map(item => item.documentId.toString())
              )
            : new Set<string>();

        for (const [documentId, sub] of this._renderSubs) {
            if (wanted.has(documentId)) continue;

            sub.unsubscribe();
            this._renderSubs.delete(documentId);
        }

        for (const documentId of wanted) {
            if (this._renderSubs.has(documentId)) continue;

            const channel = `DOCUMENT-${documentId}`;
            const sub = new Subscription();

            sub.add(
                this._pusherService
                    .subscribe(channel, 'doc-pdf-progress')
                    .subscribe((resp: { percent: number }) =>
                        this._onDocumentProgress(documentId, resp?.percent ?? 0)
                    )
            );

            // O engine avisa o fim antes do nosso worker reparar: adiantar aqui tira até
            // 15 segundos de "na fila" numa linha que já terminou.
            sub.add(
                this._pusherService
                    .subscribe(channel, 'doc-pdf-generated')
                    .subscribe(() =>
                        this._setItemStatus(documentId, DocumentBatchItemStatusEnum.COMPLETED)
                    )
            );

            sub.add(
                this._pusherService
                    .subscribe(channel, 'doc-pdf-error')
                    .subscribe(() =>
                        this._setItemStatus(documentId, DocumentBatchItemStatusEnum.ERROR)
                    )
            );

            this._renderSubs.set(documentId, sub);
        }
    }

    /** Primeiro evento do documento = a geração começou; os seguintes movem a barra. */
    private _onDocumentProgress(documentId: string, percent: number): void {
        if (!this._rendering().has(documentId)) {
            this._rendering.update(current => new Set(current).add(documentId));
        }

        this._renderProgress.update(current => ({
            ...current,
            // Nunca volta atrás: o engine manda marcos, e um evento fora de ordem faria a
            // barra do item recuar.
            [documentId]: Math.max(current[documentId] ?? 0, percent),
        }));
    }

    /** Aplica na lista local o que o canal do documento contou, sem esperar o worker. */
    private _setItemStatus(documentId: string, status: DocumentBatchItemStatusEnum): void {
        const batch = this.batch();
        if (!batch) return;

        this.batch.set({
            ...batch,
            items: batch.items.map(item =>
                item.documentId?.toString() === documentId ? { ...item, status } : item
            ),
        });

        this._rendering.update(current => {
            const next = new Set(current);
            next.delete(documentId);
            return next;
        });

        this._syncDocumentChannels();
        this._updatePill();
    }

    private _unlisten(): void {
        this._channelSub?.unsubscribe();
        this._channelSub = null;

        for (const sub of this._renderSubs.values()) sub.unsubscribe();
        this._renderSubs.clear();
        this._rendering.set(new Set());
        this._renderProgress.set({});
        this._busyItems.set(new Set());
        this.zipDone.set(0);
        this.zipTotal.set(0);
        this.zipBytes.set(0);
        this.zipTotalBytes.set(0);
    }

    private _onProgress(payload: {
        status?: DocumentBatchStatusEnum;
        zipDone?: number;
        zipTotal?: number;
        zipBytes?: number;
        zipTotalBytes?: number;
        items?: {
            documentId: string;
            status: DocumentBatchItemStatusEnum;
            error?: string;
            rendering?: boolean;
        }[];
    }): void {
        const batch = this.batch();
        if (!batch) return;

        if (payload?.zipTotal) {
            this.zipDone.set(payload.zipDone ?? 0);
            this.zipTotal.set(payload.zipTotal);
            this.zipBytes.set(payload.zipBytes ?? 0);
            this.zipTotalBytes.set(payload.zipTotalBytes ?? 0);
        }

        const byId = new Map((payload?.items ?? []).map(item => [item.documentId, item]));

        this.batch.set({
            ...batch,
            status: payload?.status ?? batch.status,
            items: batch.items.map(item => {
                const fresh = byId.get(item.documentId);
                if (!fresh) return item;

                return {
                    ...item,
                    status: fresh.status ?? item.status,
                    // O motivo vem junto do status: é ele que separa "não foi gerado" de
                    // "você tirou este do pacote" na linha da lista.
                    error: fresh.status === DocumentBatchItemStatusEnum.ERROR
                        ? (fresh.error ?? item.error)
                        : undefined,
                };
            }),
        });

        if (payload?.status === DocumentBatchStatusEnum.CANCELLED) {
            this._finishLocally(DocumentBatchStatusEnum.CANCELLED);
            return;
        }

        // A fila andou: assina os próximos e larga os que saíram.
        this._syncDocumentChannels();

        this._updatePill();
    }

    /**
     * Terminou. Rebusca o pacote porque o evento traz só o resumo, e a lista precisa
     * mostrar quais documentos ficaram de fora.
     */
    private _onCompleted(status?: DocumentBatchStatusEnum): void {
        const batch = this.batch();
        if (!batch) return;

        this._unlisten();
        this.batch.set({ ...batch, status: status ?? DocumentBatchStatusEnum.COMPLETED });

        this._controller.getById(batch._id).subscribe({
            next: fresh => fresh && this.batch.set(fresh),
        });

        // Minimizado, o aviso tem que existir fora do modal fechado: a ilha vira verde
        // com o botão de baixar, e a faixa na lista do serviço fica no lugar dela.
        if (this.minimized()) this._showReadyPill();
    }

    private _onError(): void {
        this._unlisten();
        this._dismissPill();

        const batch = this.batch();
        if (batch) this.batch.set({ ...batch, status: DocumentBatchStatusEnum.ERROR });

        this._toast.error({
            title: this._t('order.documentBatch.errorTitle'),
            message: this._t('order.documentBatch.errorMessage'),
        });
    }

    private _resync(): void {
        const batch = this.batch();
        if (!batch) return;

        this._controller.getById(batch._id).subscribe({
            next: fresh => {
                if (!fresh) return;

                this.batch.set(fresh);
                if (batchHasZip(fresh)) this._onCompleted(fresh.status);
                else if (fresh.status === DocumentBatchStatusEnum.ERROR) this._onError();
            },
        });
    }

    private _finishLocally(status: DocumentBatchStatusEnum): void {
        this._unlisten();
        this._dismissPill();
        this.minimized.set(false);

        const batch = this.batch();
        if (batch) this.batch.set({ ...batch, status });
    }

    // * ── Ilha do rodapé ───────────────────────────────────────────────────────

    private _showPill(): void {
        if (this._pillId) return;

        this._pillId = this._toast.show({
            type: 'info',
            variant: 'pill',
            icon: 'document-download',
            message: this._pillMessage(),
            persistent: true,
            dismissible: false,
            progress: this.percent(),
            action: {
                label: this._t('order.documentBatch.pillOpen'),
                handler: () => this.restore(),
            },
        });
    }

    private _updatePill(): void {
        if (!this._pillId) return;

        // Sem `progress` na montagem: o anel com 100% ao lado de "montando" é a mesma
        // contradição que a barra cheia do modal.
        this._toast.update(this._pillId, {
            message: this._pillMessage(),
            progress: this.zipping() ? undefined : this.percent(),
        });
    }

    /** A ilha muda de cara ao terminar: de progresso para "baixar", e sai sozinha. */
    private _showReadyPill(): void {
        this._dismissPill();

        const id = this._toast.show({
            type: 'success',
            variant: 'pill',
            icon: 'document-download',
            message: this._t('order.documentBatch.pillReady', { count: this.succeeded() }),
            duration: DocumentBatchService.READY_PILL_MS,
            dismissible: true,
            action: {
                label: this._t('order.documentBatch.downloadZip'),
                handler: () => this.downloadZip(),
            },
        });
        this._pillId = id;

        // O toast se fecha sozinho no fim da duração; sem soltar a referência aqui, um
        // download terminado depois disso faria a ilha reaparecer do nada.
        this._readyPillTimer = setTimeout(() => {
            if (this._pillId === id) this._pillId = null;
        }, DocumentBatchService.READY_PILL_MS + 500);
    }

    /** A ilha vira "baixando" enquanto o arquivo não chega: é o único aviso quem minimizou tem. */
    private _pillDownloading(): void {
        if (!this._pillId) return;

        this._toast.update(this._pillId, {
            message: this._t('order.documentBatch.downloading'),
            action: undefined,
        });
    }

    private _dismissPill(): void {
        if (this._readyPillTimer) {
            clearTimeout(this._readyPillTimer);
            this._readyPillTimer = null;
        }

        if (!this._pillId) return;

        this._toast.dismiss(this._pillId);
        this._pillId = null;
    }

    private _pillMessage(): string {
        if (this.zipping()) return this._t('order.documentBatch.zippingTitle');

        return this._t('order.documentBatch.pillProgress', {
            done: this.done(),
            total: this.total(),
        });
    }

    private _t(key: string, params?: Record<string, unknown>): string {
        return this._translate.instant(key, params);
    }
}
